import { useState } from 'react';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import { alpha } from '@mui/material/styles';
import axiosInstance from '../utils/axiosInstance';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

import {
  Alert,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  MenuItem,
} from '@mui/material';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

export default function Feedback(props) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [type, setType] = useState(''); 
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [allowContact, setAllowContact] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');


  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setSubmitted(false);
    setError('');

    // Basic client-side validation to match backend & DB
    if (!email.trim()) {
      setError('Email address is required.');
      setSubmitting(false);
      return;
    }
    if (!type.trim()) {
      setError('Feedback type is required.');
      setSubmitting(false);
      return;
    }
    if (!message.trim()) {
      setError('Feedback message is required.');
      setSubmitting(false);
      return;
    }

    const payload = {
      firstName,
      lastName,
      email,
      phone,
      type,
      subject,
      message,
      allowContact,
    };

    try {
      await axiosInstance.post('/feedback', payload);
      setSubmitting(false);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setSubmitting(false);
      setError('Unable to submit feedback right now. Please try again later.');
    }
  };

  const handleReset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setType('general');
    setSubject('');
    setMessage('');
    setAllowContact(true);
    setSubmitted(false);
    setError('');
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />

        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: 3 
          }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Feedback' },
            ]}
          />

          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Stack
              spacing={2}
              sx={{
                width: '100%',
                maxWidth: 900,
              }}
            >
              {/* Feedback banner */}
              <Card
                sx={(theme) => ({
                  px: 3,
                  py: 2.5,
                  borderColor: alpha(theme.palette.primary.main, 0.18),
                  background: `linear-gradient(
                    145deg,
                    ${alpha(theme.palette.primary.main, 0.08)} 0%,
                    ${alpha(theme.palette.background.paper, 0.98)} 55%,
                    ${alpha(theme.palette.info.light, 0.12)} 100%
                  )`,
                })}
              >
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Feedback
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Tell us what's working well and what we can improve in AMX
                  xConsole.
                </Typography>
              </Card>

              {/* Form */}
              <Card
                elevation={0}
                sx={(theme) => ({
                  width: '100%',
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.paper,
                })}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Submit Feedback
                  </Typography>

                  {submitted && (
                    <Alert
                      severity="success"
                      sx={{ mb: 2 }}
                    >
                      Thanks for your feedback. We've recorded your response.
                    </Alert>
                  )}

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}
                  <Box component="form" onSubmit={handleSubmit}>
                    <Stack spacing={2.5}>
                      {/* Name */}
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                      >
                        <TextField
                          label="First Name"
                          placeholder="First name"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          fullWidth
                        />
                        <TextField
                          label="Last Name"
                          placeholder="Last name"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          fullWidth
                        />
                      </Stack>

                      {/* Email */}
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                      >
                        <TextField
                          label="Email address"
                          type="email"
                          required
                          placeholder="name@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          fullWidth
                        />
                      </Stack>

                      {/* Phone */}
                      <TextField
                        label="Phone number"
                        placeholder="e.g., +1 (410) 555‑1234"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        fullWidth
                      />

                      {/* Select type of feedback */}
                      <TextField
                        select
                        label="Feedback Type"
                        required
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        fullWidth
                      >
                        <MenuItem value="bug">Bug Report</MenuItem>
                        <MenuItem value="need-category">
                          Need Category
                        </MenuItem>
                        <MenuItem value="general">General</MenuItem>
                      </TextField>

                      {/* Subject and message */}
                      <TextField
                        label="Subject"
                        placeholder="e.g., Workspace layout, navigation, performance"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        fullWidth
                      />

                      <TextField
                        label="Feedback"
                        placeholder="Share details about your experience and what we can improve."
                        required
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        multiline
                        minRows={4}
                        fullWidth
                      />

                      {/* Contact permission */}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={allowContact}
                            onChange={(e) =>
                              setAllowContact(e.target.checked)
                            }
                            color="primary"
                          />
                        }
                        label="You may contact me if you need more details"
                      />

                      {/* Submission buttons */}
                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        spacing={1}
                      >
                        <Button
                          variant="outlined"
                          onClick={handleReset}
                          disabled={submitting}
                        >
                          Reset
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={submitting}
                        >
                          {submitting ? 'Submitting...' : 'Submit'}
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Stack>
          </Box>
        </Box>
      </Box>
    </AppTheme>
  );
}
